// Live credits balance for the signed-in user, with realtime updates.
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useCredits = () => {
  const { user } = useAuth();
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setCredits(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("credits")
      .eq("user_id", user.id)
      .maybeSingle();
    // Postgres `numeric` comes back as string in some clients — coerce once.
    const raw = data?.credits;
    const num = raw === null || raw === undefined ? 0 : Number(raw);
    setCredits(Number.isFinite(num) ? num : 0);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime subscription so the balance chip updates after any spend/grant.
  useEffect(() => {
    if (!user) return;
    // Unique channel per hook instance to avoid "subscribe before on" collisions
    // when multiple components mount the hook simultaneously (or under StrictMode).
    const channelName = `profile-credits-${user.id}-${crypto.randomUUID()}`;
    const ch = supabase.channel(channelName);
    ch.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "profiles",
        filter: `user_id=eq.${user.id}`,
      },
      (payload) => {
        const raw = (payload.new as { credits?: number | string })?.credits;
        if (raw === null || raw === undefined) return;
        const n = Number(raw);
        if (Number.isFinite(n)) setCredits(n);
      },
    ).subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user]);

  return { credits, loading, refresh };
};
